import N3, {DataFactory} from 'n3';
import {Concept} from "./Concept.ts";
const { namedNode } = DataFactory;
import {TVocabulary} from "@semantical/components/chat";

const rdf = {
    namespace: 'http://www.w3.org/1999/02/22-rdf-syntax-ns#',
    type: 'http://www.w3.org/1999/02/22-rdf-syntax-ns#type'
}, rdfs = {
    label: 'http://www.w3.org/2000/01/rdf-schema#label',
    comment: 'http://www.w3.org/2000/01/rdf-schema#comment'
}, skos = {
    namespace: 'http://www.w3.org/2004/02/skos/core#',
    Concept: 'http://www.w3.org/2004/02/skos/core#Concept',
    ConceptScheme: 'http://www.w3.org/2004/02/skos/core#ConceptScheme',
    prefLabel: 'http://www.w3.org/2004/02/skos/core#prefLabel',
    altLabel: 'http://www.w3.org/2004/02/skos/core#altLabel',
    hiddenLabel: 'http://www.w3.org/2004/02/skos/core#hiddenLabel',
    broader: 'http://www.w3.org/2004/02/skos/core#broader',
    related: 'http://www.w3.org/2004/02/skos/core#related',
    narrower: 'http://www.w3.org/2004/02/skos/core#narrower',
    definition: 'http://www.w3.org/2004/02/skos/core#definition',
    scopeNote: 'http://www.w3.org/2004/02/skos/core#scopeNote',
    example: 'http://www.w3.org/2004/02/skos/core#example',
    note: 'http://www.w3.org/2004/02/skos/core#note',
    topConceptOf: 'http://www.w3.org/2004/02/skos/core#topConceptOf',
    hasTopConcept: 'http://www.w3.org/2004/02/skos/core#hasTopConcept'
};

/**
 * Represents a literal value from the RDF store with its predicate and optional language tag.
 */
export type LiteralValue = {
    value: string;
    predicateUri: string;
    language?: string;
};

/**
 * Manages a SKOS (Simple Knowledge Organization System) vocabulary backed by an N3 triple store.
 * Provides methods for creating, querying, and serializing concepts with hierarchical relationships.
 */
export default class Vocabulary {
    private readonly store: N3.Store;
    private _skosLoaded: boolean = false;

    /**
     * Creates a new Vocabulary instance wrapping an N3 store.
     * @param store - The N3 triple store to use for storing vocabulary data
     */
    constructor(store: N3.Store) {
        this.store = store;
    }

    /**
     * Loads the SKOS ontology into the store if not already loaded.
     * Fetches the ontology from /ontologies/skos/skos.ttl and stores it in a named graph.
     * @returns A promise that resolves when loading is complete (or skipped if already loaded)
     */
    async ensureSkosOntologyLoaded(): Promise<void> {
        if (this._skosLoaded) return;

        try {
            const response = await fetch('/ontologies/skos/skos.ttl');
            if (!response.ok) {
                console.warn('Failed to fetch SKOS ontology:', response.status);
                return;
            }
            const ttl = await response.text();
            await this.loadOntologyIntoGraph(ttl, skos.namespace);
            this._skosLoaded = true;
        } catch (error) {
            console.warn('Failed to load SKOS ontology:', error);
        }
    }

    /**
     * Parses Turtle data and loads it into a specific named graph.
     * @param turtleData - The Turtle-formatted RDF data to parse
     * @param graphIri - The IRI of the named graph to store the data in
     * @returns A promise that resolves when parsing and loading is complete
     */
    private loadOntologyIntoGraph(turtleData: string, graphIri: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const parser = new N3.Parser();
            const graphNode = namedNode(graphIri);

            parser.parse(turtleData, (error, quad, _prefixes) => {
                if (error) {
                    reject(error);
                } else if (quad) {
                    this.store.addQuad(quad.subject, quad.predicate, quad.object, graphNode);
                } else {
                    resolve();
                }
            });
        });
    }

    /**
     * Retrieves the rdfs:label for a subject from a specific named graph.
     * @param subjectUri - The URI of the subject to get the label for
     * @param graphIri - The IRI of the named graph to search in
     * @param preferredLang - The preferred language tag (defaults to 'en')
     * @returns The label string if found, or undefined if not found
     */
    getLabelFromGraph(subjectUri: string, graphIri: string, preferredLang: string = 'en'): string | undefined {
        const graphNode = namedNode(graphIri);
        const quads = this.store.getQuads(subjectUri, rdfs.label, null, graphNode);

        const preferred = quads.find(q => (q.object as any).language === preferredLang);
        if (preferred) return preferred.object.value;

        const any = quads.find(q => typeof q.object.value === 'string');
        return any ? any.object.value : undefined;
    }

    /**
     * Gets the label for a SKOS property URI from the loaded SKOS ontology.
     * @param propertyUri - The URI of the SKOS property (e.g., skos:broader)
     * @returns The label string if found, or undefined if not found
     */
    getSkosPropertyLabel(propertyUri: string): string | undefined {
        return this.getLabelFromGraph(propertyUri, skos.namespace);
    }

    /**
     * Serializes the vocabulary to Turtle format.
     * @returns A promise that resolves to the Turtle-formatted string representation
     */
    toTurtle(): Promise<string> {
        const writer = new N3.Writer({ prefixes: { rdf: rdf.namespace, skos: skos.namespace } });
        writer.addQuads(this.store.getQuads(null as any, null as any, null as any, null));
        return new Promise((resolve, reject) => {
            writer.end((error, result) => {
                if (error) reject(error);
                else resolve(result || "");
            });
        });
    }

    /**
     * Gets the first literal value for a subject-predicate pair, with language preference.
     * @param subject - The subject URI
     * @param predicate - The predicate URI
     * @param preferredLang - The preferred language tag (defaults to 'en')
     * @returns The literal value if found, or undefined if not found
     */
    private getFirstLiteral(subject: string, predicate: string, preferredLang: string = 'en'): string | undefined {
        // Try preferred language first
        const quads = this.store.getQuads(subject, predicate, null, null);
        const preferred = quads.find(q => (q.object as any).language === preferredLang);
        if (preferred) return preferred.object.value;
        // Fallback to any literal
        const any = quads.find(q => typeof q.object.value === 'string');
        return any ? any.object.value : undefined;
    }

    /**
     * Adds a triple to the store only if it doesn't already exist.
     * @param s - The subject URI
     * @param p - The predicate URI
     * @param o - The object URI
     */
    private addQuadIfMissing(s: string, p: string, o: string) {
        const existing = this.store.getQuads(s, p, o, null);
        if (existing.length === 0) {
            this.store.addQuad(namedNode(s), namedNode(p), namedNode(o));
        }
    }

    /**
     * Replaces all existing literal values for a subject-predicate pair with a new value.
     * @param s - The subject URI
     * @param p - The predicate URI
     * @param value - The new literal value
     * @param lang - The language tag for the literal (defaults to 'en')
     */
    private addLiteralQuadReplaceAll(s: string, p: string, value: string, lang: string = 'en') {
        const existing = this.store.getQuads(namedNode(s), namedNode(p), null, null);
        existing.forEach(q => this.store.removeQuad(q));
        this.store.addQuad(namedNode(s), namedNode(p), DataFactory.literal(value, lang));
    }

    /**
     * Checks if a concept with the given URI exists in the vocabulary.
     * @param uri - The URI to check
     * @returns True if the URI is typed as a skos:Concept, false otherwise
     */
    hasConcept(uri: string): boolean {
        return this.store.getQuads(uri, rdf.type, skos.Concept, null).length > 0;
    }

    /**
     * Converts the vocabulary to a JSON representation.
     * @returns A TVocabulary object containing all concepts as JSON
     */
    toJSON() : TVocabulary {
        return {
            concepts: this.getConcepts().map(concept => concept.toJSON())
        };
    }

    /**
     * Creates a new Vocabulary instance from Turtle-formatted data.
     * @param turtleData - The Turtle-formatted RDF data to parse
     * @returns A promise that resolves to a new Vocabulary instance
     */
    static fromTurtle(turtleData: string) : Promise<Vocabulary> {
        return new Promise((resolve, reject) => {
            const store = new N3.Store();
            const parser = new N3.Parser();

            parser.parse(turtleData, (error, quad, _prefixes) => {
                if (error) {
                    reject(error);
                } else if (quad) {
                    store.addQuad(quad);
                } else {
                    resolve(new Vocabulary(store));
                }
            });
        });
    }

    /**
     * Retrieves a Concept object for the given URI.
     * Extracts prefLabel, definition, broader, and narrower relationships from the store.
     * Narrower relations are deduced both from explicit narrower triples and inverse broader triples.
     * @param uri - The URI of the concept to retrieve
     * @returns A Concept instance populated with data from the store
     */
    getConcept(uri: string): Concept {
        const conceptName = this.getFirstLiteral(uri, skos.prefLabel) || (uri.includes('#') ? uri.split('#').pop()! : uri.split('/').pop() || uri);
        const broaderRelations = this.store.getQuads(uri, skos.broader, null, null);
        const narrowerRelations = this.store.getQuads(uri, skos.narrower, null, null);

        // deduce narrower relations based on broader relations
        const inverseBroader = this.store.getQuads(null, skos.broader, uri, null);

        const broader = Array.from(new Set(broaderRelations.map(q => q.object.value).filter(target => this.hasConcept(target))));
        const narrowers = new Set<string>(narrowerRelations.map(q => q.object.value).filter(target => this.hasConcept(target)));
        inverseBroader.forEach(q => {
            const subj = q.subject.value;
            if (this.hasConcept(subj)) narrowers.add(subj);
        });

        const definition = this.getFirstLiteral(uri, skos.definition) || "No definition provided";

        return new Concept(uri, conceptName, broader, Array.from(narrowers), definition);
    }

    /**
     * Retrieves all concepts in the vocabulary.
     * @returns An array of all Concept instances typed as skos:Concept in the store
     */
    getConcepts(): Concept[] {
        const skosConcepts = this.store.getQuads(null, rdf.type, skos.Concept, null);
        return skosConcepts.map(skosConcept => this.getConcept(skosConcept.subject.value));
    }

    /**
     * Adds a concept to the vocabulary.
     * Creates the concept type triple, prefLabel, definition (if provided), and
     * establishes broader/narrower relationships with existing concepts.
     * @param concept - The Concept instance to add
     */
    addConcept(concept: Concept) {
        this.addQuadIfMissing(concept.uri, rdf.type, skos.Concept);
        this.addLiteralQuadReplaceAll(concept.uri, skos.prefLabel, concept.prefLabel, 'en');
        // Only add a definition triple if a non-empty definition was provided
        if (concept.definition && concept.definition.trim() !== "") {
            this.addLiteralQuadReplaceAll(concept.uri, skos.definition, concept.definition, 'en');
        }

        // Broader relations and inverse narrower (only if target already exists as skos:Concept)
        concept.broader.forEach(broader => {
            if (this.hasConcept(broader)) {
                this.addQuadIfMissing(concept.uri, skos.broader, broader);
                this.addQuadIfMissing(broader, skos.narrower, concept.uri);
            }
        });
        // Narrower relations and inverse broader (only if target already exists as skos:Concept)
        concept.narrower.forEach(narrower => {
            if (this.hasConcept(narrower)) {
                this.addQuadIfMissing(concept.uri, skos.narrower, narrower);
                this.addQuadIfMissing(narrower, skos.broader, concept.uri);
            }
        });
    }

    /**
     * Updates the definition of an existing concept.
     * @param conceptUri - The URI of the concept to update
     * @param newDefinition - The new definition text
     */
    updateConceptDefinition(conceptUri: string, newDefinition: string) {
        this.addLiteralQuadReplaceAll(conceptUri, skos.definition, newDefinition, 'en');
    }

    /**
     * Updates the preferred label of an existing concept.
     * @param conceptUri - The URI of the concept to update
     * @param newPrefLabel - The new preferred label
     * @param lang - The language tag for the label (defaults to 'en')
     */
    updateConceptPrefLabel(conceptUri: string, newPrefLabel: string, lang: string = 'en') {
        this.addLiteralQuadReplaceAll(conceptUri, skos.prefLabel, newPrefLabel, lang);
    }

    /**
     * Adds a symmetric skos:related relationship between two concepts.
     * @param conceptUri - The URI of the first concept
     * @param relatedUri - The URI of the related concept
     */
    addRelated(conceptUri: string, relatedUri: string) {
        // skos:related is symmetric
        this.addQuadIfMissing(conceptUri, skos.related, relatedUri);
        this.addQuadIfMissing(relatedUri, skos.related, conceptUri);
    }

    /**
     * Removes a concept and all its relationships from the vocabulary.
     * Deletes all triples where the concept is either subject or object.
     * @param conceptUri - The URI of the concept to remove
     */
    removeConcept(conceptUri: string) {
        // Remove all triples where subject = conceptUri
        const subjectQuads = this.store.getQuads(namedNode(conceptUri), null as any, null as any, null);
        subjectQuads.forEach(q => this.store.removeQuad(q));
        // Remove all triples where object = conceptUri (cleanup inverse relations)
        const objectQuads = this.store.getQuads(null as any, null as any, namedNode(conceptUri), null);
        objectQuads.forEach(q => this.store.removeQuad(q));
    }

    /**
     * Generates a D3-compatible graph structure for visualization.
     * Creates nodes for all concepts and links for broader, narrower, and related relationships.
     * @returns An object with nodes array and links array suitable for D3 force-directed graphs
     */
    getD3Graph() {
        const nodeMap = new Map<string, { id: string, name: string, group: number }>();
        const links: { source: string, target: string, value: string }[] = [];
        const linkSet = new Set<string>();

        const skosConcepts = this.store.getQuads(null, rdf.type, skos.Concept, null);

        const addNode = (uri: string) => {
            if (!nodeMap.has(uri)) {
                const name = this.getFirstLiteral(uri, skos.prefLabel) || (uri.includes('#') ? uri.split('#').pop()! : uri.split('/').pop() || uri);
                nodeMap.set(uri, { id: uri, name, group: 1 });
            }
        };

        const addLink = (source: string, target: string, value: string) => {
            const key = `${source}|${value}|${target}`;
            if (!linkSet.has(key)) {
                linkSet.add(key);
                links.push({ source, target, value });
            }
        };

        skosConcepts.forEach(skosConcept => {
            const conceptUri = skosConcept.subject.value;
            addNode(conceptUri);

            // broader
            this.store.getQuads(conceptUri, skos.broader, null, null).forEach(q => {
                const target = q.object.value;
                if (this.hasConcept(target)) {
                    addNode(target);
                    addLink(conceptUri, target, 'broader');
                }
            });

            // narrower
            this.store.getQuads(conceptUri, skos.narrower, null, null).forEach(q => {
                const target = q.object.value;
                if (this.hasConcept(target)) {
                    addNode(target);
                    addLink(conceptUri, target, 'narrower');
                }
            });

            // related
            this.store.getQuads(conceptUri, skos.related, null, null).forEach(q => {
                const target = q.object.value;
                addNode(target);
                addLink(conceptUri, target, 'related');
            });
        });

        return {
            nodes: Array.from(nodeMap.values()),
            links
        };
    }

    /**
     * Gets all labels for a concept (prefLabel, altLabel, hiddenLabel, rdfs:label).
     * @param conceptUri - The URI of the concept
     * @returns Array of literal values with predicate URI and optional language tag
     */
    getConceptLabels(conceptUri: string): LiteralValue[] {
        const labels: LiteralValue[] = [];
        const labelPredicates = [
            skos.prefLabel,
            skos.altLabel,
            skos.hiddenLabel,
            rdfs.label
        ];

        for (const predicateUri of labelPredicates) {
            const quads = this.store.getQuads(conceptUri, predicateUri, null, null);
            for (const quad of quads) {
                const literal = quad.object as any;
                labels.push({
                    value: literal.value,
                    predicateUri,
                    language: literal.language || undefined
                });
            }
        }

        return labels;
    }

    /**
     * Gets all definitions/notes for a concept (definition, scopeNote, example, note, rdfs:comment).
     * @param conceptUri - The URI of the concept
     * @returns Array of literal values with predicate URI and optional language tag
     */
    getConceptDefinitions(conceptUri: string): LiteralValue[] {
        const definitions: LiteralValue[] = [];
        const definitionPredicates = [
            skos.definition,
            skos.scopeNote,
            skos.example,
            skos.note,
            rdfs.comment
        ];

        for (const predicateUri of definitionPredicates) {
            const quads = this.store.getQuads(conceptUri, predicateUri, null, null);
            for (const quad of quads) {
                const literal = quad.object as any;
                definitions.push({
                    value: literal.value,
                    predicateUri,
                    language: literal.language || undefined
                });
            }
        }

        return definitions;
    }

    /**
     * Gets the ConceptScheme URI if one exists in the vocabulary.
     * @returns The URI of the first ConceptScheme found, or undefined if none exists
     */
    getConceptSchemeUri(): string | undefined {
        const schemeQuads = this.store.getQuads(null, rdf.type, skos.ConceptScheme, null);
        return schemeQuads.length > 0 ? schemeQuads[0].subject.value : undefined;
    }

    /**
     * Gets literal values for specified predicates on a subject.
     * @param subjectUri - The URI of the subject
     * @param predicateUris - Array of predicate URIs to query
     * @returns Array of literal values with predicate URI and optional language tag
     */
    getSubjectLiterals(subjectUri: string, predicateUris: string[]): LiteralValue[] {
        const literals: LiteralValue[] = [];

        for (const predicateUri of predicateUris) {
            const quads = this.store.getQuads(subjectUri, predicateUri, null, null);
            for (const quad of quads) {
                const literal = quad.object as any;
                literals.push({
                    value: literal.value,
                    predicateUri,
                    language: literal.language || undefined
                });
            }
        }

        return literals;
    }

    /**
     * Gets top concept URIs for a ConceptScheme (via topConceptOf or hasTopConcept).
     * @param schemeUri - The URI of the ConceptScheme
     * @returns Array of concept URIs that are top concepts of the scheme
     */
    getTopConcepts(schemeUri: string): string[] {
        const topConcepts = new Set<string>();

        // Check for concepts with skos:topConceptOf pointing to this scheme
        const topConceptOfQuads = this.store.getQuads(null, skos.topConceptOf, schemeUri, null);
        for (const quad of topConceptOfQuads) {
            topConcepts.add(quad.subject.value);
        }

        // Also check for skos:hasTopConcept on the scheme itself
        const hasTopConceptQuads = this.store.getQuads(schemeUri, skos.hasTopConcept, null, null);
        for (const quad of hasTopConceptQuads) {
            topConcepts.add(quad.object.value);
        }

        return Array.from(topConcepts);
    }
}

export {Vocabulary};
